import { useState, useMemo } from 'react';
import { Trophy, ChevronRight, Crown, Medal, Award, Swords, Clock, Circle } from 'lucide-react';

/**
 * Tournament Bracket Visualization Component
 * Displays a knockout-style bracket for tournaments
 */

// Single Match Card in the bracket
function MatchCard({ game, playerMap, isCompleted, isFinal, round }) {
  const team1Won = isCompleted && game.team1_score > game.team2_score;
  const team2Won = isCompleted && game.team2_score > game.team1_score;
  const isPending = !isCompleted;
  
  const getTeamDisplay = (teamPlayers) => {
    if (!teamPlayers || teamPlayers.length === 0) return 'TBD';
    if (typeof teamPlayers[0] === 'string' && playerMap[teamPlayers[0]]) {
      return teamPlayers.map(id => playerMap[id] || id).join(' & ');
    }
    return teamPlayers.join(' & ');
  };

  return (
    <div 
      className={`
        relative rounded-lg border shadow-sm overflow-hidden min-w-[130px] max-w-[180px] transition-all
        ${isFinal && isCompleted ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50' :
          isPending ? 'border-dashed border-slate-300 bg-slate-50' : 'border-slate-200 bg-white'}
      `}
      data-testid={`bracket-match-${game?.id || 'tbd'}`}
    >
      {isFinal && isCompleted && (
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-md transform rotate-12">
          <Crown size={14} className="text-white -rotate-12" />
        </div>
      )}
      
      {/* Round label */}
      <div className={`text-[9px] font-medium uppercase tracking-wide px-2 py-0.5 border-b flex items-center gap-1 ${
        isFinal && isCompleted 
          ? 'bg-amber-100 text-amber-700 border-amber-200' 
          : isPending 
          ? 'bg-slate-100 text-slate-500 border-slate-200'
          : 'bg-slate-50 text-slate-500 border-slate-100'
      }`}>
        {isPending && <Clock size={10} className="text-slate-400" />}
        {isFinal ? (isCompleted ? 'Final' : 'Final (Pending)') : `Round ${round}`}
        {isPending && !isFinal && <span className="ml-auto text-[9px] text-slate-400">Not played</span>}
      </div>
      
      {/* Team 1 */}
      <div className={`px-2 py-1.5 flex items-center justify-between gap-1.5 border-b border-slate-100 ${
        team1Won ? 'bg-green-50' : team2Won ? 'opacity-50' : ''
      }`}>
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {team1Won && <Trophy size={9} className="text-green-600 flex-shrink-0" />}
          {isPending && <Circle size={8} className="text-slate-300 flex-shrink-0" />}
          <span className={`text-[10px] truncate ${team1Won ? 'font-bold text-green-700' : isPending ? 'text-slate-400' : 'text-slate-700'}`}>
            {game ? getTeamDisplay(game.team1_players || game.team1_player_names) : 'TBD'}
          </span>
        </div>
        {isCompleted && (
          <span className={`text-[10px] font-mono font-bold flex-shrink-0 ${team1Won ? 'text-green-700' : 'text-slate-400'}`}>
            {game.team1_score}
          </span>
        )}
        {isPending && <span className="text-[9px] text-slate-300 font-mono">-</span>}
      </div>

      {/* Team 2 */}
      <div className={`px-2 py-1.5 flex items-center justify-between gap-1.5 ${
        team2Won ? 'bg-green-50' : team1Won ? 'opacity-50' : ''
      }`}>
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {team2Won && <Trophy size={9} className="text-green-600 flex-shrink-0" />}
          {isPending && <Circle size={8} className="text-slate-300 flex-shrink-0" />}
          <span className={`text-[10px] truncate ${team2Won ? 'font-bold text-green-700' : isPending ? 'text-slate-400' : 'text-slate-700'}`}>
            {game ? getTeamDisplay(game.team2_players || game.team2_player_names) : 'TBD'}
          </span>
        </div>
        {isCompleted && (
          <span className={`text-[10px] font-mono font-bold flex-shrink-0 ${team2Won ? 'text-green-700' : 'text-slate-400'}`}>
            {game.team2_score}
          </span>
        )}
        {isPending && <span className="text-[9px] text-slate-300 font-mono">-</span>}
      </div>
    </div>
  );
}

// Connector line between rounds
function BracketConnector() {
  return (
    <div className="flex items-center justify-center w-8">
      <div className="h-px w-full bg-slate-300" />
      <ChevronRight size={14} className="text-slate-400 -ml-1 flex-shrink-0" />
    </div>
  );
}

// Main Bracket Component
export default function TournamentBracket({ games, players, standings, gameType }) {
  const [showFull, setShowFull] = useState(false);
  const isSingles = gameType === 'singles';
  
  // Organize games into rounds based on creation order or any round markers
  const bracketData = useMemo(() => {
    if (!games || games.length === 0) return null;
    
    const playerMap = {};
    players?.forEach(p => {
      const dname = p.display_name || p.name;
      const rating = p.tournament_rating;
      playerMap[p.id] = rating ? `${dname} (${rating})` : dname;
    });
    
    // Sort games by created_at or id to determine rounds
    const sortedGames = [...games].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB;
    });
    
    // Count completed games
    const completedCount = sortedGames.filter(g => g.status === 'completed').length;
    
    // Group into rounds (simple heuristic: every N games is a round)
    const rounds = [];
    let currentRound = [];
    let roundSize = Math.ceil(games.length / 2) || 1;
    
    sortedGames.forEach((game) => {
      currentRound.push(game);
      if (currentRound.length >= roundSize && rounds.length < 3) {
        rounds.push([...currentRound]);
        currentRound = [];
        roundSize = Math.ceil(roundSize / 2);
      }
    });
    
    if (currentRound.length > 0) {
      rounds.push(currentRound);
    }
    
    // Build winning teams for podium (for doubles, show team names)
    const winningTeams = [];
    sortedGames.filter(g => g.status === 'completed').forEach(g => {
      const t1Won = g.team1_score > g.team2_score;
      const winningPlayers = t1Won 
        ? (g.team1_player_names || g.team1_players?.map(id => playerMap[id] || id) || [])
        : (g.team2_player_names || g.team2_players?.map(id => playerMap[id] || id) || []);
      const teamName = Array.isArray(winningPlayers) ? winningPlayers.join(' & ') : winningPlayers;
      if (teamName && !winningTeams.includes(teamName)) {
        winningTeams.push(teamName);
      }
    });
    
    return { rounds, playerMap, completedCount, winningTeams };
  }, [games, players]);
  
  if (!bracketData || bracketData.rounds.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Swords className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">No games yet</p>
        <p className="text-sm text-slate-400 mt-1">Generate games to see the bracket</p>
      </div>
    );
  }

  // Only show podium if there are completed games with meaningful results
  const hasCompletedGames = bracketData.completedCount > 0;
  
  // For doubles: use standings but show team context
  // For singles: use standings directly
  const topThree = hasCompletedGames ? standings?.slice(0, 3) : [];
  
  // Build podium display names - for doubles, try to find partner pairs from games
  const getPodiumDisplay = (playerEntry, rank) => {
    if (isSingles) {
      const n = playerEntry?.display_name || playerEntry?.player_name || `${rank}`;
      return { name: n, initials: n[0] || rank };
    }
    
    // For doubles, find the player's partner from completed games
    const playerId = playerEntry?.player_id;
    if (!playerId) return { name: `${rank}`, initials: rank };
    
    // Search through completed games to find this player's team
    let partnerName = null;
    for (const game of games || []) {
      if (game.status !== 'completed') continue;
      
      const t1Ids = game.team1_player_ids || [];
      const t2Ids = game.team2_player_ids || [];
      const t1Names = game.team1_player_names || game.team1_players || [];
      const t2Names = game.team2_player_names || game.team2_players || [];
      
      if (t1Ids.includes(playerId) && t1Ids.length === 2) {
        const partnerIdx = t1Ids.indexOf(playerId) === 0 ? 1 : 0;
        partnerName = typeof t1Names[partnerIdx] === 'string' ? t1Names[partnerIdx] : bracketData.playerMap[t1Ids[partnerIdx]];
        break;
      }
      if (t2Ids.includes(playerId) && t2Ids.length === 2) {
        const partnerIdx = t2Ids.indexOf(playerId) === 0 ? 1 : 0;
        partnerName = typeof t2Names[partnerIdx] === 'string' ? t2Names[partnerIdx] : bracketData.playerMap[t2Ids[partnerIdx]];
        break;
      }
    }
    
    const mainName = playerEntry?.display_name || playerEntry?.player_name || '?';
    if (partnerName && partnerName !== mainName) {
      return { 
        name: `${mainName} & ${partnerName}`,
        initials: `${mainName[0]}${partnerName[0]}`.toUpperCase()
      };
    }
    return { name: mainName, initials: mainName[0]?.toUpperCase() || rank };
  };

  return (
    <div className="space-y-6">
      {/* No completed games message */}
      {!hasCompletedGames && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
          <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
          <p className="text-sm text-blue-700 font-medium">No games have been completed yet</p>
          <p className="text-xs text-blue-500 mt-1">Complete games to see results and standings</p>
        </div>
      )}

      {/* Bracket Visualization */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Swords className="w-5 h-5 text-green-600" />
            <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Match Bracket</h3>
            <span className="text-xs text-slate-400 ml-2">
              {bracketData.completedCount}/{games.length} completed
            </span>
          </div>
          <button
            onClick={() => setShowFull(!showFull)}
            className="text-xs text-green-600 hover:text-green-700 font-medium"
          >
            {showFull ? 'Collapse' : 'Expand All'}
          </button>
        </div>
        
        <div className="p-2 overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-max">
            {bracketData.rounds.map((roundGames, roundIndex) => (
              <div key={roundIndex} className="flex items-center">
                {/* Round column */}
                <div className="flex flex-col gap-2">
                  {roundGames.slice(0, showFull ? undefined : 4).map((game) => (
                    <MatchCard
                      key={game.id}
                      game={game}
                      playerMap={bracketData.playerMap}
                      isCompleted={game.status === 'completed'}
                      isFinal={roundIndex === bracketData.rounds.length - 1 && roundGames.length === 1}
                      round={roundIndex + 1}
                    />
                  ))}
                  {!showFull && roundGames.length > 4 && (
                    <button
                      onClick={() => setShowFull(true)}
                      className="text-xs text-slate-500 hover:text-green-600 font-medium py-2"
                    >
                      +{roundGames.length - 4} more matches
                    </button>
                  )}
                </div>
                
                {/* Connector to next round */}
                {roundIndex < bracketData.rounds.length - 1 && <BracketConnector />}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-6 text-xs text-slate-500">
          <span className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-300" />
            Winner
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-slate-100 border border-dashed border-slate-300" />
            Pending
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-amber-100 border border-amber-300" />
            Final (Completed)
          </span>
        </div>
      </div>
    </div>
  );
}
